import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, updateDoc, doc, deleteField } from "firebase/firestore";
import { CheckCircle, PauseCircle, Save, ArrowRight } from "lucide-react";

import { auth, db } from "../../firebase.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";
import PhotoUpload from "../SignupService/PhotoUpload.js";
import {
  CATEGORIES,
  PROVINCES,
  GESCHIKT_VOOR,
  PRIJS_MODELLEN,
  SOORT_ACT,
  SOORT_CATERING,
  FOTO_VIDEO_DIENSTEN,
  STYLING_SPECIALISATIES,
  VOERTUIG_SOORTEN,
  REISKOSTEN,
  STRAAL_OPTIES,
  detailsForCategory,
  prune,
} from "../../service.js";

// Zelfbediening-editor voor diensten. Spiegelt de velden uit de onboarding,
// maar dan als één pagina zodat een dienst na de (lichte) aanmelding de rest van
// de vermelding kan aanvullen en later bijwerken.
function ServiceEdit() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const [id, setId] = useState("");
  const [service, setService] = useState(null);

  const [categorie, setCategorie] = useState("");
  const [bedrijfsnaam, setBedrijfsnaam] = useState("");
  const [beschrijving, setBeschrijving] = useState("");
  const [contact, setContact] = useState({ email: "", contactpersoon: "", telefoon: "", website: "", kvk: "" });

  const [werkgebiedMode, setWerkgebiedMode] = useState("landelijk");
  const [provincies, setProvincies] = useState([]);
  const [vestiging, setVestiging] = useState({ postcode: "", plaats: "", provincie: "" });
  const [straalKm, setStraalKm] = useState(50);
  const [reiskosten, setReiskosten] = useState("");

  const [geschiktVoor, setGeschiktVoor] = useState([]);
  const [prijsmodel, setPrijsmodel] = useState("");
  const [bedrag, setBedrag] = useState("");
  const [media, setMedia] = useState({ logo: "", video: "" });
  const [photos, setPhotos] = useState([]);

  const [soortAct, setSoortAct] = useState("");
  const [soortCatering, setSoortCatering] = useState([]);
  const [diensten, setDiensten] = useState([]);
  const [specialisaties, setSpecialisaties] = useState([]);
  const [assortiment, setAssortiment] = useState([""]);
  const [voertuigen, setVoertuigen] = useState([{ soort: "limousine", capaciteit: "" }]);
  const [dienst, setDienst] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, "services"), where("uid", "==", uid)));
        if (snap.empty) {
          if (!cancelled) setLoading(false);
          return;
        }
        const first = snap.docs[0];
        const data = first.data();
        if (cancelled) return;

        setId(first.id);
        setService({ id: first.id, ...data });
        setCategorie(data.categorie || "");
        setBedrijfsnaam(data.bedrijfsnaam || "");
        setBeschrijving(data.beschrijving || "");
        if (data.contact) setContact({ email: "", contactpersoon: "", telefoon: "", website: "", kvk: "", ...data.contact });

        if (data.werkgebied) {
          const w = data.werkgebied;
          if (w.landelijk) setWerkgebiedMode("landelijk");
          else if (w.straalKm) setWerkgebiedMode("straal");
          else setWerkgebiedMode("provincies");
          setProvincies(w.provincies || []);
          if (w.straalKm) setStraalKm(w.straalKm);
          setReiskosten(w.reiskosten || "");
        }
        if (data.vestigingsplaats) {
          setVestiging({
            postcode: data.vestigingsplaats.postcode || "",
            plaats: data.vestigingsplaats.plaats || "",
            provincie: data.vestigingsplaats.provincie || "",
          });
        }
        if (data.geschiktVoor) setGeschiktVoor(data.geschiktVoor);
        if (data.media) setMedia({ logo: data.media.logo || "", video: data.media.video || "" });
        if (data.media?.fotos?.length) setPhotos(data.media.fotos.map((url) => ({ url, path: null })));
        if (data.prijsindicatie) {
          setPrijsmodel(data.prijsindicatie.model || "");
          if (data.prijsindicatie.bedrag != null) setBedrag(String(data.prijsindicatie.bedrag));
        }

        const d = data.details || {};
        if (d.soortAct) setSoortAct(d.soortAct);
        if (d.soortCatering) setSoortCatering(d.soortCatering);
        if (d.diensten) setDiensten(d.diensten);
        if (d.specialisaties) setSpecialisaties(d.specialisaties);
        if (d.assortiment?.length) setAssortiment(d.assortiment.map((a) => a.naam || ""));
        if (d.voertuigen?.length) setVoertuigen(d.voertuigen.map((v) => ({ soort: v.soort || "limousine", capaciteit: String(v.capaciteit ?? "") })));
        if (d.dienst) setDienst(d.dienst);
      } catch (err) {
        console.log(err);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(list, setList, value) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  async function onSave(e) {
    e.preventDefault();
    setError("");
    setSaved(false);

    if (!bedrijfsnaam.trim()) return setError("Vul je bedrijfsnaam in.");
    if (beschrijving.trim().length < 20) return setError("Geef een beschrijving van minimaal 20 tekens.");
    if (!contact.email.trim()) return setError("Vul je contactemail in.");

    let werkgebied;
    let vestigingsplaats;
    if (werkgebiedMode === "provincies") {
      if (!provincies.length) return setError("Kies minimaal één provincie.");
      werkgebied = { landelijk: false, provincies };
    } else if (werkgebiedMode === "straal") {
      const postcode = vestiging.postcode.trim().toUpperCase().replace(/\s+/g, " ");
      if (!/^[1-9][0-9]{3}\s?[A-Z]{2}$/.test(postcode)) return setError("Vul een geldige postcode in (bijv. 1234 AB).");
      if (!vestiging.plaats.trim()) return setError("Vul je plaats in.");
      if (!vestiging.provincie) return setError("Kies je provincie.");
      werkgebied = prune({ landelijk: false, straalKm, reiskosten });
      vestigingsplaats = prune({ postcode, plaats: vestiging.plaats.trim(), provincie: vestiging.provincie });
    } else {
      werkgebied = { landelijk: true };
    }

    const details = detailsForCategory(categorie, { soortAct, soortCatering, diensten, specialisaties, assortiment, voertuigen, dienst });
    if (!details) return setError("Vul de gevraagde gegevens voor je categorie in.");

    const bedragNum = Number(bedrag);
    const bedragVereist = prijsmodel && prijsmodel !== "offerte";
    if (bedragVereist && (!bedrag || !Number.isFinite(bedragNum) || bedragNum < 0)) {
      return setError("Vul een geldig bedrag in bij het gekozen prijsmodel, of kies 'offerte'.");
    }
    const fotos = photos.map((p) => p.url);
    const prijsindicatie = prijsmodel ? prune({ model: prijsmodel, bedrag: bedragVereist ? bedragNum : undefined, valuta: "EUR" }) : undefined;

    setSaving(true);
    try {
      await updateDoc(doc(db, "services", id), {
        categorie,
        bedrijfsnaam: bedrijfsnaam.trim(),
        beschrijving: beschrijving.trim(),
        contact: prune(contact),
        werkgebied,
        vestigingsplaats: vestigingsplaats || deleteField(),
        details,
        ...prune({ media: { logo: media.logo, fotos, video: media.video }, geschiktVoor, prijsindicatie }),
      });
      setSaved(true);
    } catch (err) {
      console.log(err);
      setError(err.message || "Opslaan is mislukt. Probeer het opnieuw.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="card p-6 text-center max-w-3xl mx-auto">
        <div className="w-6 h-6 mx-auto mb-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-text-secondary">Je dienst laden...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="card p-6 text-center max-w-3xl mx-auto">
        <h2 className="text-lg font-heading font-bold mb-2">Nog geen dienstprofiel</h2>
        <p className="text-sm text-text-secondary mb-4">We konden geen dienstprofiel vinden voor dit account.</p>
        <Link to="/signup-service">
          <Button label="Aanmelden als dienst" icon={ArrowRight} />
        </Link>
      </div>
    );
  }

  const isPublished = service.advertentie?.status === "gepubliceerd" || service.published;

  return (
    <form onSubmit={onSave} className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold">Mijn dienst</h1>
          <p className="text-sm text-text-secondary mt-0.5">Beheer de gegevens van je vermelding op Feestjegeven.nl</p>
        </div>
        {isPublished ? (
          <span className="badge bg-success-subtle text-success"><CheckCircle size={12} className="mr-1" />Online</span>
        ) : (
          <span className="badge bg-warning-subtle text-warning"><PauseCircle size={12} className="mr-1" />Niet online</span>
        )}
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">Bedrijf</h2>
        <div>
          <label className="label-base">Categorie</label>
          <select value={categorie} onChange={(e) => setCategorie(e.target.value)} className="input-base">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-base">Bedrijfsnaam <span className="text-error">*</span></label>
          <input type="text" value={bedrijfsnaam} onChange={(e) => setBedrijfsnaam(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label-base">Beschrijving <span className="text-error">*</span></label>
          <textarea value={beschrijving} onChange={(e) => setBeschrijving(e.target.value)} rows={4} className="input-base" placeholder="Vertel wat je aanbiedt (min. 20 tekens)" />
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">Contact</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-base">Contactemail <span className="text-error">*</span></label>
            <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="input-base" />
          </div>
          <div>
            <label className="label-base">Contactpersoon</label>
            <input type="text" value={contact.contactpersoon} onChange={(e) => setContact({ ...contact, contactpersoon: e.target.value })} className="input-base" />
          </div>
          <div>
            <label className="label-base">Telefoon</label>
            <input type="tel" value={contact.telefoon} onChange={(e) => setContact({ ...contact, telefoon: e.target.value })} className="input-base" />
          </div>
          <div>
            <label className="label-base">Website</label>
            <input type="text" value={contact.website} onChange={(e) => setContact({ ...contact, website: e.target.value })} placeholder="https://" className="input-base" />
          </div>
          <div>
            <label className="label-base">KvK-nummer</label>
            <input type="text" value={contact.kvk} onChange={(e) => setContact({ ...contact, kvk: e.target.value })} placeholder="8 cijfers" className="input-base" />
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">Werkgebied</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { value: "landelijk", label: "Heel Nederland" },
            { value: "provincies", label: "Provincies" },
            { value: "straal", label: "Straal rond mijn plaats" },
          ].map((m) => (
            <button key={m.value} type="button" onClick={() => setWerkgebiedMode(m.value)} className={["px-3 py-1.5 rounded-full border text-sm", werkgebiedMode === m.value ? "bg-accent-subtle border-accent/30 text-accent" : "bg-bg-surface border-border text-text-secondary"].join(" ")}>{m.label}</button>
          ))}
        </div>
        {werkgebiedMode === "provincies" && (
          <div className="flex flex-wrap gap-2">
            {PROVINCES.map((p) => (
              <button key={p} type="button" onClick={() => toggle(provincies, setProvincies, p)} className={["px-3 py-1.5 rounded-full border text-sm", provincies.includes(p) ? "bg-accent-subtle border-accent/30 text-accent" : "bg-bg-surface border-border text-text-secondary"].join(" ")}>{p}</button>
            ))}
          </div>
        )}
        {werkgebiedMode === "straal" && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-base">Postcode <span className="text-error">*</span></label>
                <input className="input-base" value={vestiging.postcode} onChange={(e) => setVestiging({ ...vestiging, postcode: e.target.value })} placeholder="1234 AB" />
              </div>
              <div>
                <label className="label-base">Plaats <span className="text-error">*</span></label>
                <input className="input-base" value={vestiging.plaats} onChange={(e) => setVestiging({ ...vestiging, plaats: e.target.value })} placeholder="bijv. Utrecht" />
              </div>
              <div>
                <label className="label-base">Provincie <span className="text-error">*</span></label>
                <select className="input-base" value={vestiging.provincie} onChange={(e) => setVestiging({ ...vestiging, provincie: e.target.value })}>
                  <option value="">Kies...</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="label-base">Straal</label>
                <select className="input-base" value={straalKm} onChange={(e) => setStraalKm(Number(e.target.value))}>
                  {STRAAL_OPTIES.map((km) => <option key={km} value={km}>{km} km</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="label-base">Reiskosten</label>
              <select className="input-base" value={reiskosten} onChange={(e) => setReiskosten(e.target.value)}>
                <option value="">Niet opgegeven</option>
                {REISKOSTEN.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">Details</h2>
        {categorie === "muziek-entertainment" && (
          <div>
            <label className="label-base">Soort act <span className="text-error">*</span></label>
            <select value={soortAct} onChange={(e) => setSoortAct(e.target.value)} className="input-base"><option value="">Kies...</option>{SOORT_ACT.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </div>
        )}
        {categorie === "catering-food" && <ChipGroup label="Soort catering" options={SOORT_CATERING} selected={soortCatering} onToggle={(v) => toggle(soortCatering, setSoortCatering, v)} />}
        {categorie === "foto-video" && <ChipGroup label="Diensten" options={FOTO_VIDEO_DIENSTEN} selected={diensten} onToggle={(v) => toggle(diensten, setDiensten, v)} />}
        {categorie === "styling-decoratie" && <ChipGroup label="Specialisaties" options={STYLING_SPECIALISATIES} selected={specialisaties} onToggle={(v) => toggle(specialisaties, setSpecialisaties, v)} />}
        {categorie === "verhuur" && (
          <div>
            <label className="label-base">Assortiment <span className="text-error">*</span></label>
            {assortiment.map((a, i) => (
              <input key={i} className="input-base mb-2" placeholder="Artikelnaam" value={a} onChange={(e) => setAssortiment(assortiment.map((x, j) => (j === i ? e.target.value : x)))} />
            ))}
            <button type="button" className="text-sm text-accent font-medium" onClick={() => setAssortiment([...assortiment, ""])}>+ Artikel toevoegen</button>
          </div>
        )}
        {categorie === "vervoer" && (
          <div>
            <label className="label-base">Voertuigen <span className="text-error">*</span></label>
            {voertuigen.map((v, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                <select className="input-base" value={v.soort} onChange={(e) => setVoertuigen(voertuigen.map((x, j) => (j === i ? { ...x, soort: e.target.value } : x)))}>{VOERTUIG_SOORTEN.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                <input className="input-base" type="number" placeholder="Capaciteit" value={v.capaciteit} onChange={(e) => setVoertuigen(voertuigen.map((x, j) => (j === i ? { ...x, capaciteit: e.target.value } : x)))} />
              </div>
            ))}
            <button type="button" className="text-sm text-accent font-medium" onClick={() => setVoertuigen([...voertuigen, { soort: "limousine", capaciteit: "" }])}>+ Voertuig toevoegen</button>
          </div>
        )}
        {categorie === "overige-diensten" && (
          <div>
            <label className="label-base">Dienst <span className="text-error">*</span></label>
            <input className="input-base" value={dienst} onChange={(e) => setDienst(e.target.value)} placeholder="bijv. ceremoniespreker" />
          </div>
        )}
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">Media & prijs</h2>
        <div>
          <label className="label-base">Logo (URL)</label>
          <input className="input-base" value={media.logo} onChange={(e) => setMedia({ ...media, logo: e.target.value })} placeholder="https://" />
        </div>
        <div>
          <label className="label-base">Foto's</label>
          <PhotoUpload serviceId={id} photos={photos} onChange={setPhotos} />
        </div>
        <div>
          <label className="label-base">Video (URL)</label>
          <input className="input-base" value={media.video} onChange={(e) => setMedia({ ...media, video: e.target.value })} placeholder="https://" />
        </div>
        <ChipGroup label="Geschikt voor" options={GESCHIKT_VOOR} selected={geschiktVoor} onToggle={(v) => toggle(geschiktVoor, setGeschiktVoor, v)} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-base">Prijsmodel</label>
            <select className="input-base" value={prijsmodel} onChange={(e) => setPrijsmodel(e.target.value)}><option value="">Geen</option>{PRIJS_MODELLEN.map((m) => <option key={m} value={m}>{m}</option>)}</select>
          </div>
          {prijsmodel && prijsmodel !== "offerte" && (
            <div>
              <label className="label-base">Bedrag (€)</label>
              <input className="input-base" type="number" value={bedrag} onChange={(e) => setBedrag(e.target.value)} />
            </div>
          )}
        </div>
      </div>

      {error && <Message text={error} />}
      {saved && <Message variant="success" text="Je wijzigingen zijn opgeslagen." />}

      <div className="flex items-center gap-3">
        <Button type="submit" label="Wijzigingen opslaan" pending={saving} loadingLabel="Opslaan..." icon={Save} />
      </div>
    </form>
  );
}

function ChipGroup({ label, options, selected, onToggle }) {
  return (
    <div>
      <label className="label-base mb-2 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onToggle(o)} className={["px-3 py-1.5 rounded-full border text-sm", selected.includes(o) ? "bg-accent-subtle border-accent/30 text-accent" : "bg-bg-surface border-border text-text-secondary"].join(" ")}>{o}</button>
        ))}
      </div>
    </div>
  );
}

export default ServiceEdit;
